/**
 * A state-variable filter in the trapezoidal (TPT) form: unlike a naive biquad it stays
 * stable while its cutoff is swept every few samples, which is what blasts and whooshes do.
 */

import type { FilterSpec } from './patch';

export class Svf {
  private ic1 = 0;
  private ic2 = 0;
  private a1 = 0;
  private a2 = 0;
  private a3 = 0;
  private k = 1;
  /** Band-pass make-up: keeps filtered NOISE at about the same loudness whatever the Q. */
  private bpGain = 1;

  set(cutoff: number, q: number, sampleRate: number): void {
    const fc = Math.min(sampleRate * 0.45, Math.max(10, cutoff));
    const g = Math.tan((Math.PI * fc) / sampleRate);
    this.k = 1 / Math.max(0.3, q);
    this.bpGain = Math.sqrt(this.k);
    this.a1 = 1 / (1 + g * (g + this.k));
    this.a2 = g * this.a1;
    this.a3 = g * this.a2;
  }

  process(x: number, type: FilterSpec['type']): number {
    const v3 = x - this.ic2;
    const v1 = this.a1 * this.ic1 + this.a2 * v3;
    const v2 = this.ic2 + this.a2 * this.ic1 + this.a3 * v3;
    this.ic1 = 2 * v1 - this.ic1;
    this.ic2 = 2 * v2 - this.ic2;
    if (type === 'lp') return v2;
    if (type === 'bp') return v1 * this.bpGain;
    return x - this.k * v1 - v2;
  }
}
